import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { X, Download, Tag, Gift, Sparkles, ChevronRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { rewardsService } from '../../services/firebaseConfig';
import { Voucher, UserVoucher } from '../../types';
import { useScrollLock } from '../../hooks/useScrollLock';
import toast from 'react-hot-toast';

const STORAGE_KEY = 'baakas_welcome_dismissed';
const SESSION_KEY = 'baakas_welcome_shown_session';

const WelcomePopup: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [availableVouchers, setAvailableVouchers] = useState<Voucher[]>([]);
  const [userVouchers, setUserVouchers] = useState<UserVoucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimingVoucher, setClaimingVoucher] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const backdropRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const vouchersContainerRef = useRef<HTMLDivElement>(null);
  
  // Lock scroll without causing layout shift
  useScrollLock(isOpen);

  // Animate modal
  useEffect(() => {
    if (isOpen) {
      if (backdropRef.current) {
        gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2 });
      }
      if (modalRef.current) {
        gsap.fromTo(
          modalRef.current,
          { scale: 0.9, opacity: 0, y: 20 },
          { scale: 1, opacity: 1, y: 0, duration: 0.3, ease: "back.out(1.7)" }
        );
      }
    } else {
      if (backdropRef.current) {
        gsap.to(backdropRef.current, { opacity: 0, duration: 0.2 });
      }
      if (modalRef.current) {
        gsap.to(modalRef.current, { scale: 0.9, opacity: 0, y: 20, duration: 0.3, ease: "power2.in" });
      }
    }
  }, [isOpen]);

  // Animate voucher cards with stagger
  useEffect(() => {
    if (isOpen && !loading && availableVouchers.length > 0 && vouchersContainerRef.current) {
      const cards = vouchersContainerRef.current.querySelectorAll('[data-voucher-card]');
      gsap.fromTo(
        cards,
        { opacity: 0, x: -20 },
        { 
          opacity: 1, 
          x: 0, 
          duration: 0.3, 
          stagger: 0.1,
          ease: "power2.out"
        }
      );
    }
  }, [isOpen, loading, availableVouchers]);

  useEffect(() => {
    // Only show on home page
    if (location.pathname !== '/') {
      return;
    }

    // Check if user has dismissed the popup permanently
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed === 'true') {
      return;
    }

    // Check if already shown in this session
    const shownThisSession = sessionStorage.getItem(SESSION_KEY);
    if (shownThisSession === 'true') {
      return;
    }

    // Small delay to ensure page is loaded
    const timer = setTimeout(() => {
      setIsOpen(true);
      sessionStorage.setItem(SESSION_KEY, 'true');
    }, 1000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Fetch vouchers when popup opens
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchVouchers = async () => {
      setLoading(true);
      try {
        // Fetch available vouchers
        const available = await rewardsService.getActiveVouchers();
        setAvailableVouchers(available);

        // If user is logged in, fetch their claimed vouchers
        if (currentUser?.uid) {
          const userVouchersData = await rewardsService.getUserVouchers(currentUser.uid);
          setUserVouchers(userVouchersData);
        }
      } catch (error) {
        // Silent fail - don't show error for popup
      } finally {
        setLoading(false);
      }
    };

    fetchVouchers();
  }, [isOpen, currentUser?.uid]);

  // Handle voucher claiming
  const handleClaimVoucher = async (voucherId: string) => {
    if (!currentUser?.uid) {
      // Redirect to login if not logged in
      handleClose();
      toast('Please log in to claim coupons', { icon: '🔐' });
      navigate('/login', { state: { from: location.pathname, claimVoucher: voucherId } });
      return;
    }

    try {
      setClaimingVoucher(voucherId);
      await rewardsService.claimVoucher(currentUser.uid, voucherId);
      toast.success('Coupon claimed successfully!');
      
      // Refresh user vouchers
      const updatedUserVouchers = await rewardsService.getUserVouchers(currentUser.uid);
      setUserVouchers(updatedUserVouchers);
    } catch (error: any) {
      toast.error(error.message || 'Failed to claim coupon');
    } finally {
      setClaimingVoucher(null);
    }
  };

  // Handle claim all vouchers
  const handleClaimAll = async () => {
    if (!currentUser?.uid) {
      handleClose();
      toast('Please log in to claim coupons', { icon: '🔐' });
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    const unclaimedVouchers = availableVouchers.filter(v => !claimedVoucherIds.has(v.id));
    if (unclaimedVouchers.length === 0) {
      toast.success('All coupons already claimed!');
      return;
    }

    try {
      setClaimingVoucher('all');
      for (const voucher of unclaimedVouchers) {
        try {
          await rewardsService.claimVoucher(currentUser.uid, voucher.id);
        } catch {
          // Continue with other vouchers if one fails
        }
      }
      toast.success(`${unclaimedVouchers.length} coupons claimed!`);
      
      // Refresh user vouchers
      const updatedUserVouchers = await rewardsService.getUserVouchers(currentUser.uid);
      setUserVouchers(updatedUserVouchers);
    } catch (error: any) {
      toast.error('Failed to claim some coupons');
    } finally {
      setClaimingVoucher(null);
    }
  };

  // Get claimed voucher IDs (excluding expired and used vouchers)
  const claimedVoucherIds = new Set(
    userVouchers
      .filter((uv) => {
        // Exclude used vouchers
        if (uv.isUsed) return false;
        
        // Check expiration - exclude expired vouchers
        const now = new Date();
        const expiresAt = uv.expiresAt?.toDate?.() || uv.voucher?.expiresAt?.toDate?.() || null;
        if (expiresAt && expiresAt < now) {
          return false;
        }
        
        return true;
      })
      .map((uv) => uv.voucherId)
  );

  const handleClose = () => {
    setIsOpen(false);
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  };

  // Format discount display
  const formatDiscount = (voucher: Voucher) => {
    if (voucher.discountType === 'percentage') {
      return `${voucher.discountValue}%`;
    } else if (voucher.discountType === 'free_delivery') {
      return 'FREE';
    } else {
      return `Rs.${voucher.discountValue}`;
    }
  };

  // Get discount label
  const getDiscountLabel = (voucher: Voucher) => {
    if (voucher.discountType === 'free_delivery') {
      return 'Free Delivery';
    } else if (voucher.discountType === 'percentage') {
      return 'OFF';
    } else {
      return 'OFF';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      style={{ willChange: 'opacity' }}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-popup-title"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden relative"
      >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 z-10 w-8 h-8 bg-black/10 hover:bg-black/20 text-gray-600 hover:text-gray-800 rounded-full flex items-center justify-center transition-all"
              aria-label="Close popup"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Decorative Header with Gift */}
            <div className="relative bg-gradient-to-br from-[#da2516] via-[#ff4433] to-[#da2516] pt-6 pb-8 px-6 overflow-hidden">
              {/* Sparkle decorations */}
              <div className="absolute top-2 left-8 text-yellow-300 animate-pulse">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="absolute top-6 right-12 text-yellow-300 animate-pulse delay-100">
                <Sparkles className="w-3 h-3" />
              </div>
              <div className="absolute bottom-4 left-16 text-yellow-300 animate-pulse delay-200">
                <Sparkles className="w-3 h-3" />
              </div>
              <div className="absolute top-4 left-1/2 text-yellow-300 animate-pulse delay-300">
                <Sparkles className="w-4 h-4" />
              </div>
              
              {/* Gift icon with ribbon */}
              <div className="flex justify-center mb-3">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg transform rotate-3">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  {/* Ribbon effect */}
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-6 h-4 bg-yellow-300 rounded-t-full"></div>
                </div>
              </div>
              
              <h2
                id="welcome-popup-title"
                className="text-xl sm:text-2xl font-bold text-white text-center font-poppins"
              >
                Special Deals Just for You
              </h2>
              <p className="text-white/80 text-sm text-center mt-1">
                {!currentUser ? 'Coupons confirmed after login' : 'Claim your exclusive coupons'}
              </p>
            </div>

            {/* Coupons Section */}
            <div className="p-4 max-h-[300px] overflow-y-auto">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : availableVouchers.length > 0 ? (
                <div ref={vouchersContainerRef} className="space-y-3">
                  {availableVouchers.slice(0, 4).map((voucher, index) => {
                    const isClaimed = claimedVoucherIds.has(voucher.id);
                    const isClaimingThis = claimingVoucher === voucher.id || claimingVoucher === 'all';
                    
                    return (
                      <div
                        key={voucher.id}
                        data-voucher-card
                        className={`relative flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                          isClaimed 
                            ? 'border-green-200 bg-green-50' 
                            : 'border-dashed border-orange-300 bg-orange-50/50 hover:border-orange-400 hover:bg-orange-50'
                        }`}
                      >
                        {/* New User Badge */}
                        {index === 0 && !isClaimed && (
                          <div className="absolute -top-2 left-3 px-2 py-0.5 bg-[#da2516] text-white text-[10px] font-semibold rounded">
                            New User
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3">
                          {/* Discount Value */}
                          <div className="text-center min-w-[60px]">
                            <div className="text-2xl font-bold text-[#da2516]">
                              {formatDiscount(voucher)}
                            </div>
                            <div className="text-xs text-[#da2516] font-medium">
                              {getDiscountLabel(voucher)}
                            </div>
                          </div>
                          
                          {/* Divider */}
                          <div className="h-12 w-px bg-orange-200"></div>
                          
                          {/* Coupon Details */}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-800 text-sm truncate">
                              {voucher.title || voucher.code}
                            </div>
                            {voucher.minPurchaseAmount && voucher.minPurchaseAmount > 0 ? (
                              <div className="text-xs text-gray-500">
                                Min. purchase Rs.{voucher.minPurchaseAmount}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500">No min. purchase</div>
                            )}
                            {voucher.maxDiscountAmount && (
                              <div className="text-xs text-gray-400">
                                Max Rs.{voucher.maxDiscountAmount}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Claim Status/Button */}
                        {isClaimed ? (
                          <div className="flex items-center gap-1 text-green-600 text-xs font-medium">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Claimed
                          </div>
                        ) : (
                          <button
                            onClick={() => handleClaimVoucher(voucher.id)}
                            disabled={isClaimingThis}
                            className="px-3 py-1.5 bg-[#da2516] hover:bg-[#b91f12] disabled:bg-gray-300 text-white text-xs font-medium rounded-full transition-colors flex items-center gap-1"
                          >
                            {isClaimingThis ? (
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <Tag className="w-3 h-3" />
                                Claim
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No coupons available right now</p>
                  <p className="text-gray-400 text-xs mt-1">Check back soon for new deals!</p>
                </div>
              )}
            </div>

            {/* Collect All Button */}
            {availableVouchers.length > 0 && (
              <div className="px-4 pb-4">
                <button
                  onClick={handleClaimAll}
                  disabled={claimingVoucher === 'all' || availableVouchers.every(v => claimedVoucherIds.has(v.id))}
                  className="w-full py-3 bg-black hover:bg-gray-800 disabled:bg-gray-300 text-white font-semibold rounded-full transition-colors flex items-center justify-center gap-2"
                >
                  {claimingVoucher === 'all' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Claiming...
                    </>
                  ) : availableVouchers.every(v => claimedVoucherIds.has(v.id)) ? (
                    <>All Claimed!</>
                  ) : (
                    <>
                      Collect All
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                {!currentUser && (
                  <p className="text-center text-xs text-gray-500 mt-2">
                    Login to collect and use your coupons
                  </p>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-gray-200" />

            {/* App Download Section */}
            <div className="p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <Download className="w-4 h-4 text-[#da2516]" />
                <h3 className="text-sm font-semibold text-gray-800">Get the Baakas App</h3>
              </div>
              <div className="flex items-center justify-center gap-3">
                <a
                  href="https://apps.apple.com/np/app/baakas-online-shopping-app/id6755933632"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-opacity hover:opacity-80"
                  aria-label="Download Baakas app from App Store"
                  onClick={handleClose}
                >
                  <img
                    src="/assets/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                    alt="Download on the App Store"
                    className="h-10 w-auto"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.src = "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1704067200";
                    }}
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.baakas.buyer&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-opacity hover:opacity-80"
                  aria-label="Download Baakas app from Google Play Store"
                  onClick={handleClose}
                >
                  <img
                    src="/assets/GetItOnGooglePlay_Badge_Web_color_English.png"
                    alt="Get it on Google Play"
                    className="h-10 w-auto"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.src = "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png";
                    }}
                  />
                </a>
              </div>
            </div>

            {/* Don't show again */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="w-4 h-4 text-[#da2516] border-gray-300 rounded focus:ring-[#da2516] focus:ring-2"
                />
                <span className="text-xs text-gray-500">
                  Don't show this again
                </span>
              </label>
            </div>
          </div>
    </div>
  );
};

export default WelcomePopup;